import { ReactNode } from 'react'
import { CenteredSpinner } from './CenteredSpinner'

type Resolve<T> = { [K in keyof T]: NonNullable<T[K]> }

export interface LoadingProps<T extends Record<string, unknown>> {
  /**
   * Object of properties that should all resolve to truthy when children should
   * be rendered. Boolean can also be passed to do a simple true/false check
   * until children are rendered.
   */
  until: T | null | undefined | boolean
  /**
   * Render children when until is truthy, passing resolved object containing
   * `NonNullable` properties.
   */
  children: (resolve: Resolve<T>) => ReactNode
  /**
   * Fallback component, defaults to `<CenteredSpinner />`.
   */
  fallback?: () => ReactNode
}

export const Loading = <T extends Record<string, unknown>>({
  children,
  until,
  fallback = () => <CenteredSpinner />,
}: LoadingProps<T>) => {
  if (!until) {
    return fallback()
  }
  if (typeof until === 'object') {
    // Verify all values in object are truthy.
    const isReady = Object.values(until).every(Boolean)
    return isReady ? children(until as Resolve<T>) : fallback()
  }
  return until ? children({} as Resolve<T>) : fallback()
}
